import { useReactiveVar } from '@bestseller/retail-planning.utils.reactive-vars';
import { useKeyboardShortcut } from '@bestseller-bit/frontend-community.hooks.use-keyboard-shortcut';
import { Box, Stack, styled, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Container from 'src/components/atoms/Container';
import Loader from 'src/components/atoms/Loader';
import Select from 'src/components/atoms/Select';
import Searchbar from 'src/components/molecules/Searchbar';
import StoreSelectionTabs from 'src/components/molecules/Tabs/StoreSelectionTabs';
import SelectionContainer from 'src/components/organisms/SelectionContainer/SelectionContainer';
import { useFilterContext } from 'src/contexts/FilterContext';
import { GroupLevel, UnsavedChangesStorageKey } from 'src/domain';
import { useStores } from 'src/hooks/store-selection/queries/useStores';
import { useStoreType } from 'src/hooks/useStoreType';
import { selectedPartnerVar } from 'src/infrastructure/local_state';
import { StoreType } from 'src/infrastructure/rest-api/api-types';

export const StoreSelection: FC = () => {
    const [filterContext, setFilterContext] = useFilterContext();
    const [_selectedPartner, setSelectedPartner] = useReactiveVar(selectedPartnerVar);
    const storeType = useStoreType();
    const [searchValue, setSearchValue] = useState(filterContext.searchQuery.storeSelection);
    const [debouncedSearchValue, setDebouncedSearchValue] = useState(filterContext.searchQuery.storeSelection);

    // Keep track of previous store type - this is to avoid an inifinite loop when listening for changes on the store type with useEffect
    const previousStoreType = useRef<StoreType | undefined>(storeType);

    useEffect(() => {
        if (previousStoreType.current !== storeType) {
            // When store type changes we need to reset clusters and partners on the filter context otherwise we risk:
            // 1. filtering on locked fields that we cannot clear
            // 2. filtering with data that is not relevant to the current store type
            // in both cases we may end up with no results and in the first case we can never get out of this state again.
            setFilterContext({
                ...filterContext,
                clusters: [],
                partners: [],
            });
        }
        previousStoreType.current = storeType;
    }, [filterContext, storeType, setFilterContext]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedSearchValue(searchValue);
        }, 500);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [searchValue]);

    const storesFilterContext = useMemo(() => {
        return {
            ...filterContext,
            searchQuery: {
                ...filterContext.searchQuery,
                storeSelection: debouncedSearchValue,
            },
        };
    }, [debouncedSearchValue, filterContext]);

    const { data, loading, error } = useStores(filterContext.grouping, storeType, storesFilterContext);
    const { groupedStores, storeCount } = data;

    const noResults = !loading && storeCount <= 0;
    const showStores = !loading && storeCount > 0;

    const handleActiveGroup = useCallback(
        (activeGroup: string) => {
            setFilterContext({
                ...filterContext,
                grouping: activeGroup as GroupLevel,
            });
        },
        [filterContext, setFilterContext]
    );

    const clearUserData = useCallback(() => {
        // Remove all unsaved changes when the user visits store selection page
        if (sessionStorage.getItem(UnsavedChangesStorageKey)) {
            sessionStorage.removeItem(UnsavedChangesStorageKey);
        }

        setSelectedPartner(undefined);
    }, [setSelectedPartner]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: we only want to run on mount, not everytime the function changes
    useEffect(() => {
        clearUserData();
    }, []);

    const handleUserInput = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setSearchValue(event.target.value);
    };

    const handleResetInputField = () => {
        setSearchValue('');
    };

    const searchInputRef = useRef<HTMLInputElement>(null);

    useKeyboardShortcut({
        shortcutName: 'SEARCH',
        callback: (event) => {
            event.preventDefault();
            event.stopPropagation();
            // Focus the search input when the user presses Alt + S
            // This is useful for users who want to quickly search for stores without using the mouse
            searchInputRef.current?.focus();
        },
    });

    useKeyboardShortcut({
        shortcutName: 'CLEAR',
        callback: (event) => {
            event.preventDefault();
            event.stopPropagation();
            // Clear the search input when the user presses Alt + C
            // This is useful for users who want to quickly clear the search input without using the mouse
            setSearchValue('');
        },
    });

    return (
        <Box
            sx={{
                display: 'flex',
                height: 1,
            }}
        >
            <StyledStoreSelectionView>
                <Stack
                    direction="row"
                    sx={{
                        gap: 2,
                        alignItems: 'flex-end',
                        pt: 2,
                        width: '100%',
                    }}
                >
                    <Searchbar
                        inputRef={searchInputRef}
                        placeholder={'Search for stores'}
                        updateUserInput={(e) => handleUserInput(e)}
                        resetInputField={() => handleResetInputField()}
                        value={searchValue}
                    />
                </Stack>
                <List width="100%" height="100%">
                    {loading && <Loader />}
                    {!loading && (
                        <>
                            <Stack
                                direction="row"
                                sx={{
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    width: '100%',
                                }}
                            >
                                <StoreSelectionTabs />
                                <Select
                                    label="Group by"
                                    size="medium"
                                    activeItem={filterContext.grouping}
                                    options={[GroupLevel.Country, GroupLevel.Cluster]}
                                    onSelect={handleActiveGroup}
                                />
                            </Stack>

                            {noResults && (
                                <Typography
                                    sx={{
                                        textAlign: 'center',
                                    }}
                                >
                                    {error?.message ?? '0 results found'}
                                </Typography>
                            )}
                            {showStores && groupedStores && (
                                <SelectionContainer stores={groupedStores} storeType={storeType} />
                            )}
                        </>
                    )}
                </List>
            </StyledStoreSelectionView>
        </Box>
    );
};

const StyledStoreSelectionView = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    flexFlow: 'column',
    rowGap: theme.spacing(2.5),
    flexGrow: 1,
}));

const List = styled(Container)<{ width: string; height: string }>(({ theme, width, height }) => ({
    display: 'flex',
    flexFlow: 'column',
    rowGap: theme.spacing(8),
    minWidth: 640,
    width: width,
    height: height,
    minHeight: 50,
    padding: theme.spacing(2, 5, 4, 5),
    boxSizing: 'border-box',
}));
